// BM-H398 - the first screen. It cannot be skipped.
//
// Writes all four capacity gate fields the engine reads unguarded
// (`age_18_plus`, `free_and_unpressured`, `understands_will`,
// `eng_wal_domiciled`), plus whether this is a single will or a mirror pair.
// A "no" is a routing outcome, not a rejection: a fee earner calls.

import { QuestionRow, SectionIntro, Select, TextInput, YesNo } from "./fields";
import { newPerson } from "./willsPayload";

const GATE_QUESTIONS = [
  ["age_18_plus", "capacity_gate.age_18_plus", "Are you 18 or over?"],
  ["free_and_unpressured", "capacity_gate.free_and_unpressured", "Are you making this will freely, without pressure from anyone?"],
  ["understands_will", "capacity_gate.understands_will", "Do you understand that a will sets out who receives your property when you die?"],
  ["eng_wal_domiciled", "capacity_gate.eng_wal_domiciled", "Do you live in England or Wales, with no significant assets outside the UK?"],
];

function GateBlock({ gate, onChange, testidPrefix, heading }) {
  return (
    <div data-testid={`${testidPrefix}-block`}>
      {heading && <h3 style={{ fontSize: 16, margin: "16px 0 8px" }}>{heading}</h3>}
      {GATE_QUESTIONS.map(([field, qid, label]) => (
        <QuestionRow key={field} qid={qid} label={label} testid={`${testidPrefix}-q-${field}`}>
          <YesNo testid={`${testidPrefix}-${field}`} value={gate[field]} onChange={(v) => onChange({ ...gate, [field]: v })} />
        </QuestionRow>
      ))}
      <QuestionRow qid="capacity_gate.known_capacity_concerns" label="Has anyone raised a concern about your memory or decision making?" sensitive testid={`${testidPrefix}-q-known_capacity_concerns`}>
        <YesNo testid={`${testidPrefix}-known_capacity_concerns`} value={gate.known_capacity_concerns} onChange={(v) => onChange({ ...gate, known_capacity_concerns: v })} />
      </QuestionRow>
    </div>
  );
}

export default function CapacityGateScreen({ payload, update }) {
  const sub = payload.submission;
  const mode = payload.couple.mode;
  const partnerId = sub.submission.partner_person_id;
  const partner = payload.couple.people_register.find((p) => p.id === partnerId);

  function setMode(next) {
    update((d) => {
      d.couple.mode = next;
      d.submission.submission.role = next === "mirror" ? "mirror_a" : "single";
      if (next === "mirror" && !d.submission.submission.partner_person_id) {
        const p = newPerson({ relationship: "spouse" });
        d.couple.people_register.push(p);
        d.submission.submission.partner_person_id = p.id;
        d.submission.mirror.b_capacity_gate = { age_18_plus: null, free_and_unpressured: null, understands_will: null, eng_wal_domiciled: null, known_capacity_concerns: null };
      }
      if (next !== "mirror") {
        d.submission.mirror.b_capacity_gate = null;
      }
    });
  }

  return (
    <div data-testid="capacity-gate-screen">
      <SectionIntro title="Before we start">
        A few questions we have to ask everyone before the questionnaire opens. If any answer is no, that is not a problem and nothing is refused. One of our fee earners will call you.
      </SectionIntro>

      <QuestionRow qid="capacity_gate.couple_mode" label="Is this will for you alone, or are you and your partner making mirror wills together?">
        <Select
          testid="capacity-gate-couple-mode"
          value={mode}
          options={[{ value: "single", label: "For me alone" }, { value: "mirror", label: "Mirror wills for both of us" }]}
          onChange={setMode}
        />
      </QuestionRow>

      {mode === "mirror" && (
        <div data-testid="capacity-gate-partner">
          <QuestionRow qid="person.full_legal_name" label="Your partner's full legal name" testid="capacity-gate-q-partner-name">
            <TextInput
              testid="capacity-gate-partner-name"
              value={partner?.full_legal_name}
              onChange={(v) => update((d) => {
                const p = d.couple.people_register.find((x) => x.id === partnerId);
                if (p) p.full_legal_name = v;
              })}
            />
          </QuestionRow>
        </div>
      )}

      {mode && (
        <GateBlock
          gate={sub.capacity_gate}
          onChange={(g) => update((d) => { d.submission.capacity_gate = { ...d.submission.capacity_gate, ...g }; })}
          testidPrefix="capacity-gate-a"
          heading={mode === "mirror" ? `About you${payload.couple.people_register[0]?.full_legal_name ? ` (${payload.couple.people_register[0].full_legal_name})` : ""}` : null}
        />
      )}

      {mode === "mirror" && sub.mirror.b_capacity_gate && (
        <GateBlock
          gate={sub.mirror.b_capacity_gate}
          onChange={(g) => update((d) => { d.submission.mirror.b_capacity_gate = { ...d.submission.mirror.b_capacity_gate, ...g }; })}
          testidPrefix="capacity-gate-b"
          heading={`About ${partner?.full_legal_name || "your partner"}`}
        />
      )}
    </div>
  );
}
